import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useEffect, useState } from "react";
import {
  OfficeLocationAlias,
  OfficeLocationDictionaryModel,
} from "../../models/officeLocation.model";
import { OfficeLocationAdminService } from "../../services/admin";

interface AliasManagementProps {
  officeLocationService: OfficeLocationAdminService;
}

export function AliasManagement({
  officeLocationService,
}: AliasManagementProps) {
  const [officeLocations, setOfficeLocations] = useState<
    OfficeLocationDictionaryModel[]
  >([]);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
    null
  );
  const [aliases, setAliases] = useState<OfficeLocationAlias[]>([]);
  const [loading, setLoading] = useState(false);
  const [aliasesLoading, setAliasesLoading] = useState(false);
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set());

  const [opened, { open, close }] = useDisclosure(false);

  const form = useForm({
    initialValues: {
      aliasText: "",
    },
    validate: {
      aliasText: (value: string) =>
        !value?.trim() ? "Alias text is required" : null,
    },
  });

  const loadOfficeLocations = async () => {
    setLoading(true);
    try {
      const locations =
        await officeLocationService.getOfficeLocationsDictionary();
      setOfficeLocations(locations.filter((loc) => !loc.requiresAdminReview));
    } catch (error) {
      console.error("Error loading office locations:", error);
      notifications.show({
        title: "Error",
        message: "Failed to load office locations",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAliases = async (locationId: number) => {
    setAliasesLoading(true);
    try {
      const locationAliases = await officeLocationService.getAliases(
        locationId
      );
      setAliases(locationAliases);
    } catch (error) {
      console.error("Error loading aliases:", error);
      notifications.show({
        title: "Error",
        message: "Failed to load aliases",
        color: "red",
      });
      setAliases([]);
    } finally {
      setAliasesLoading(false);
    }
  };

  const handleLocationChange = (value: string | null) => {
    setSelectedLocationId(value);
    if (value) {
      loadAliases(parseInt(value));
    } else {
      setAliases([]);
    }
  };

  const handleAddAlias = async (values: { aliasText: string }) => {
    if (!selectedLocationId) return;

    try {
      const newAlias = await officeLocationService.addAlias({
        officeLocationId: parseInt(selectedLocationId),
        aliasText: values.aliasText.trim(),
      });

      setAliases((prev) => [...prev, newAlias]);
      form.reset();
      close();

      notifications.show({
        title: "Alias Added",
        message: `Alias "${values.aliasText}" was added successfully`,
        color: "green",
      });
    } catch (error) {
      console.error("Error adding alias:", error);
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to add alias",
        color: "red",
      });
    }
  };

  const handleRemoveAlias = async (alias: OfficeLocationAlias) => {
    setDeletingIds((prev) => new Set(prev).add(alias.id));

    try {
      const success = await officeLocationService.removeAlias(alias.id);

      if (success) {
        setAliases((prev) => prev.filter((a) => a.id !== alias.id));
        notifications.show({
          title: "Alias Removed",
          message: `Alias "${alias.aliasText}" was removed successfully`,
          color: "green",
        });
      } else {
        notifications.show({
          title: "Error",
          message: "Failed to remove alias",
          color: "red",
        });
      }
    } catch (error) {
      console.error("Error removing alias:", error);
      notifications.show({
        title: "Error",
        message:
          error instanceof Error ? error.message : "Failed to remove alias",
        color: "red",
      });
    } finally {
      setDeletingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(alias.id);
        return newSet;
      });
    }
  };

  useEffect(() => {
    loadOfficeLocations();
  }, []);

  const selectedLocation = officeLocations.find(
    (loc) => loc.id.toString() === selectedLocationId
  );

  return (
    <Stack>
      <div>
        <Title order={3}>Alias Management</Title>
        <Text size="sm" c="dimmed">
          Manage alternative names and spellings for office locations to improve
          discovery accuracy
        </Text>
      </div>

      <Card padding="md" withBorder>
        <Stack>
          <Select
            label="Select Office Location"
            placeholder="Choose an office location to manage aliases"
            data={officeLocations.map((location) => ({
              value: location.id.toString(),
              label: location.name,
            }))}
            value={selectedLocationId}
            onChange={handleLocationChange}
            searchable
            clearable
            disabled={loading}
          />

          {selectedLocationId && selectedLocation && (
            <>
              <Group justify="space-between" align="center">
                <div>
                  <Text fw={600} size="md" mb="xs">
                    Aliases for "{selectedLocation.name}"
                  </Text>
                  <Text size="sm" c="dimmed">
                    These alternative names will match to this location during
                    discovery
                  </Text>
                </div>
                <Button
                  leftSection="+"
                  variant="light"
                  size="sm"
                  onClick={open}
                >
                  Add Alias
                </Button>
              </Group>

              {aliasesLoading ? (
                <Group justify="center" py="md">
                  <Loader size="sm" />
                  <Text size="sm" c="dimmed">
                    Loading aliases...
                  </Text>
                </Group>
              ) : aliases.length === 0 ? (
                <Text size="sm" c="dimmed" ta="center" py="md">
                  No aliases found for this location. Add some alternative names
                  to improve discovery.
                </Text>
              ) : (
                <Stack gap="xs">
                  {aliases.map((alias) => (
                    <Card key={alias.id} padding="sm" withBorder>
                      <Group justify="space-between" align="center">
                        <div>
                          <Text size="sm" fw={500}>
                            {alias.aliasText}
                          </Text>
                          <Group gap="xs">
                            <Badge size="xs" variant="light" color="blue">
                              Normalized: {alias.normalizedAliasText}
                            </Badge>
                          </Group>
                        </div>
                        <ActionIcon
                          color="red"
                          variant="light"
                          size="sm"
                          onClick={() => handleRemoveAlias(alias)}
                          loading={deletingIds.has(alias.id)}
                          disabled={deletingIds.has(alias.id)}
                        >
                          🗑️
                        </ActionIcon>
                      </Group>
                    </Card>
                  ))}
                </Stack>
              )}
            </>
          )}
        </Stack>
      </Card>

      <Modal opened={opened} onClose={close} title="Add New Alias" size="md">
        <form onSubmit={form.onSubmit(handleAddAlias)}>
          <Stack>
            <TextInput
              label="Alias Text"
              placeholder="Enter alternative name or spelling"
              required
              {...form.getInputProps("aliasText")}
              description="This text will be normalized and used for matching during discovery"
            />

            <Group justify="flex-end">
              <Button variant="light" onClick={close}>
                Cancel
              </Button>
              <Button type="submit">Add Alias</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
